import { useAuthStore } from '@/store/use-auth-store';
import { consumePendingTenantScopeSync } from '@/utils/switch-tenant';
import { syncAuthScopeFromCurrentUser } from '@/utils/sync-auth-scope';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useActiveTenant } from './useActiveTenant';

/**
 * When the URL tenant slug changes, refresh cohort scope and tenant-scoped cache.
 * Skips the initial mount so a normal page load does not invalidate everything,
 * except to refresh the scope (without invalidating) after a tenant switch.
 */
export function useActiveTenantScopeSync(enabled: boolean) {
  const { tenantSlug } = useActiveTenant();
  const authMethod = useAuthStore((state) => state.authMethod);
  const queryClient = useQueryClient();
  const previousSlugRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || !tenantSlug || authMethod === 'sso') {
      return;
    }

    const previousSlug = previousSlugRef.current;
    previousSlugRef.current = tenantSlug;

    if (previousSlug === null) {
      if (consumePendingTenantScopeSync()) {
        syncAuthScopeFromCurrentUser().catch((error) => {
          console.error('Failed to sync scope after tenant switch', error);
        });
      }
      return;
    }

    if (previousSlug === tenantSlug) {
      return;
    }

    void (async () => {
      try {
        await syncAuthScopeFromCurrentUser();
        await queryClient.invalidateQueries();
      } catch (error) {
        console.error('Failed to sync scope for tenant route change', error);
        previousSlugRef.current = previousSlug;
      }
    })();
  }, [enabled, tenantSlug, authMethod, queryClient]);
}
